import { api } from './client'
import type {
  FinopsReport,
  FinopsReportListResponse,
  FinopsAlertsListResponse,
  FinopsAckResponse,
  FinopsSummaryKpis,
  FinopsTagBreakdown,
  FinopsForecastResponse,
  FinopsForecastCurrentResponse,
  FinopsOrphanAction,
  FinopsOrphanActionListResponse,
  FinopsOrphanRecommendBody,
  FinopsUntaggedByAccount,
} from '@/types/finops'

export interface FinopsSummary {
  tenant_id: number
  ou_id: string
  current_month_cost_usd: number
  comparison_basis: 'same_period_mtd'
  completed_days: number
  current_period_comparable_usd: number
  same_period_last_month_usd: number
  previous_month_cost_usd: number
  mom_pct: number | null
  period_label: string
  budget: {
    has_threshold: boolean
    threshold_usd: number
    pct_used: number
    exceeded: boolean
    alert: boolean
  }
  savings_potential_usd: number
  rightsizing_count: number
  cost_available: boolean
}

export interface CostByServiceItem {
  service: string
  cost_usd: number
}

export interface DailyCostItem {
  date: string
  cost_usd: number
}

export interface IdleResource {
  ebs_volumes: { volume_id: string; size_gb: number; monthly_cost_usd: number; created_at: string }[]
  elastic_ips: { allocation_id: string; public_ip: string; monthly_cost_usd: number }[]
  old_snapshots: { snapshot_id: string; size_gb: number; age_days: number; monthly_cost_usd: number }[]
  total_monthly_savings_usd: number
}

export interface SavingsProjection {
  cenario: string
  desconto_pct: number
  economia_anual_usd: number
  custo_anual_usd: number
  upfront_usd: number
  compromisso_mensal_usd: number
  melhor_roi: boolean
}

export interface RightsizingRec {
  instance_id: string
  name: string
  instance_type: string
  recommended_type: string
  avg_cpu_pct: number
  max_cpu_pct: number
  monthly_saving_usd: number
  reason: string
}

// ============================================================
// SP4 — Motor de recomendação unificado (finops_recommendations)
// Rotas canônicas: /api/v1/tenants/{tid}/finops/recommendations/*
// Plataforma: /api/v1/finops/recommendations(+/savings)
// ============================================================

/** Status do ciclo de vida de uma recomendação (CHECK no backend). */
export type FinopsRecommendationStatus =
  | 'detected'
  | 'recommended'
  | 'approved'
  | 'executing'
  | 'executed'
  | 'verified'
  | 'failed'
  | 'cancelled'
  | 'dismissed'

export type FinopsRecommendationLevel = 'high' | 'medium' | 'low'

/**
 * Row de `finops_recommendations` (backend retorna `SELECT *`).
 * Campos NUMERIC podem vir como número ou string conforme o driver — o
 * frontend normaliza ao formatar.
 */
export interface FinopsRecommendation {
  id: number
  tenant_id: number
  tenant_name?: string
  account_id: string
  region: string
  rec_type: string
  resource_id: string
  status: FinopsRecommendationStatus
  executable: boolean
  confidence: FinopsRecommendationLevel
  risk: FinopsRecommendationLevel
  estimated_monthly_savings_usd: number | string | null
  realized_monthly_savings_usd: number | string | null
  detection_payload: Record<string, unknown> | null
  verification_payload: Record<string, unknown> | null
  create_snapshot_before_delete: boolean
  recommended_by: number | null
  approved_by: number | null
  last_detected_at: string | null
  recommended_at: string | null
  approved_at: string | null
  executed_at: string | null
  verified_at: string | null
  attempt_count: number
  last_attempt_at: string | null
  last_execute_idempotency_key: string | null
  error_message: string | null
  created_at: string
}

export interface FinopsRecommendationListResponse {
  items: FinopsRecommendation[]
  next_cursor: string | null
  has_more: boolean
}

export interface FinopsRecommendationListParams {
  rec_type?: string
  status?: string
  account_id?: string
  min_savings?: number
  cursor?: string
  limit?: number
}

/** Resposta das rotas mutativas por-tenant (recommend/approve/cancel/dismiss). */
export interface FinopsRecommendationActionResponse {
  rec_id: number
  status: FinopsRecommendationStatus
}

/** Resposta de execute (202 + idempotency_key). */
export interface FinopsRecommendationExecuteResponse {
  rec_id: number
  status: 'executing'
  idempotency_key: string
}

/** Resposta de refresh por-tenant. */
export interface FinopsRecommendationRefreshResponse {
  tenant_id: number
  detected_count: number
}

/** Economia agregada (plataforma; podada por scope). */
export interface FinopsRecommendationSavings {
  estimated_monthly_savings_usd: number
  realized_monthly_savings_usd: number
  active_count: number
  pending_count: number
}

export const finopsApi = {
  getSummary: (tid: number) =>
    api.get<FinopsSummary>(`/v1/tenants/${tid}/finops/summary`).then(r => r.data),

  getCostByService: (tid: number, month?: string) =>
    api.get<{ services: CostByServiceItem[]; total_usd: number }>(`/v1/tenants/${tid}/finops/cost-by-service`, { params: { month } }).then(r => r.data),

  getDailyCost: (tid: number, month?: string) =>
    api.get<{ daily: DailyCostItem[] }>(`/v1/tenants/${tid}/finops/daily-cost`, { params: { month } }).then(r => r.data),

  getRightsizing: (tid: number) =>
    api.get<{ recommendations: RightsizingRec[]; total: number }>(`/v1/tenants/${tid}/rightsizing`).then(r => r.data),

  getSavingsPlans: (tid: number) =>
    api.get<{ projections: SavingsProjection[]; monthly_cost_usd: number; cost_available: boolean }>(`/v1/tenants/${tid}/finops/savings-plans`).then(r => r.data),

  getIdleResources: (tid: number) =>
    api.get<IdleResource>(`/v1/tenants/${tid}/finops/idle-resources`).then(r => r.data),

  // ============================================================
  // Wave 2 — /api/v1/finops router (FinopsReport / FinopsAlert)
  // ============================================================

  /**
   * Lista relatórios semanais (paginação offset).
   * Backend: GET /api/v1/finops/reports
   */
  getReports: (params?: { limit?: number; offset?: number }) =>
    api
      .get<FinopsReportListResponse>('/v1/finops/reports', { params })
      .then(r => r.data.items),

  /**
   * Detalhe de relatório com dados extraídos do payload (S3 markdown incluso).
   * Backend: GET /api/v1/finops/reports/{id}?top_n=N
   */
  getReport: (id: number, topN?: number) =>
    api
      .get<FinopsReport>(`/v1/finops/reports/${id}`, {
        params: topN != null ? { top_n: topN } : undefined,
      })
      .then(r => r.data),

  /**
   * Lista alertas FinOps (eventos não-reconhecidos por default? backend não força — caller filtra).
   * Backend: GET /api/v1/finops/alerts
   */
  getAlerts: (params?: {
    severity?: 'low' | 'medium' | 'high'
    aws_account_id?: string
    event_source?: string
    principal_type?: string
    acknowledged?: boolean
    cursor?: string
    limit?: number
  }) =>
    api
      .get<FinopsAlertsListResponse>('/v1/finops/alerts', { params })
      .then(r => r.data),

  /**
   * Reconhece (ACK) um alerta. Idempotente.
   * Backend: PATCH /api/v1/finops/alerts/{id}/ack
   * Nota: backend não aceita `note` no payload — assinatura preserva o parâmetro
   * para retrocompat de chamadas, mas é ignorado server-side.
   */
  ackAlert: (id: number, _note?: string) =>
    api
      .patch<FinopsAckResponse>(`/v1/finops/alerts/${id}/ack`)
      .then(r => r.data),

  /**
   * KPIs agregados de alertas (contagens por severity, total unacked).
   * Backend: GET /api/v1/finops/summary
   */
  getAlertsSummary: () =>
    api.get<FinopsSummaryKpis>('/v1/finops/summary').then(r => r.data),

  // ============================================================
  // S6 — Showback / Forecast / Orphan Actions / Budget
  // ============================================================

  /**
   * Custo agregado por tag (Project ou Environment) extraido do payload do report.
   * Backend: GET /api/v1/finops/reports/{id}/showback?tag=...&top_n=N
   */
  getShowback: (reportId: number, tag: 'project' | 'environment', topN = 10) =>
    api
      .get<FinopsTagBreakdown>(`/v1/finops/reports/${reportId}/showback`, {
        params: { tag, top_n: topN },
      })
      .then(r => r.data),

  /**
   * Custo sem tag agrupado por conta AWS (Cost Explorer ao vivo).
   * Backend: GET /api/v1/finops/showback/untagged-by-account?ym=YYYY-MM&tag=project
   * Retorna lista ordenada por custo desc. ym default = mês corrente (server-side).
   */
  getUntaggedByAccount: (ym?: string, tag: 'project' | 'environment' = 'project') =>
    api
      .get<FinopsUntaggedByAccount>('/v1/finops/showback/untagged-by-account', {
        params: { ...(ym ? { ym } : {}), tag },
      })
      .then(r => r.data),

  /**
   * Forecast com projecao semanal + intervalo confianca.
   * Backend: GET /api/v1/finops/reports/{id}/forecast
   * Pode retornar `{ forecast: null }` quando historico insuficiente.
   */
  getForecast: (reportId: number) =>
    api
      .get<FinopsForecastResponse>(`/v1/finops/reports/${reportId}/forecast`)
      .then(r => r.data),

  /**
   * Forecast corrente: relatório válido mais recente ou fallback ao vivo.
   * Backend: GET /api/v1/finops/forecast/current
   * Retorna source='report' (do último relatório com forecast válido) ou
   * source='live_fallback' (regressão ao vivo quando nenhum relatório tem forecast).
   */
  getCurrentForecast: () =>
    api
      .get<FinopsForecastCurrentResponse>('/v1/finops/forecast/current')
      .then(r => r.data),

  /**
   * Recomenda cleanup de orphan resource. Cria FinopsOrphanAction status=recommended.
   * Backend: POST /api/v1/tenants/{tid}/finops/idle-resources/recommend
   */
  recommendOrphan: (tenantId: number, body: FinopsOrphanRecommendBody) =>
    api
      .post<FinopsOrphanAction>(
        `/v1/tenants/${tenantId}/finops/idle-resources/recommend`,
        body,
      )
      .then(r => r.data),

  /**
   * Aprova orphan action (requer permission finops:approve_cleanup).
   * Backend: POST /api/v1/tenants/{tid}/finops/idle-resources/{id}/approve
   */
  approveOrphan: (tenantId: number, actionId: number) =>
    api
      .post<FinopsOrphanAction>(
        `/v1/tenants/${tenantId}/finops/idle-resources/${actionId}/approve`,
      )
      .then(r => r.data),

  /**
   * Dispara execucao da deleção (status=executing). Idempotency-Key opcional.
   * Backend: POST /api/v1/tenants/{tid}/finops/idle-resources/{id}/execute
   */
  executeOrphan: (
    tenantId: number,
    actionId: number,
    idempotencyKey?: string,
  ) =>
    api
      .post<FinopsOrphanAction>(
        `/v1/tenants/${tenantId}/finops/idle-resources/${actionId}/execute`,
        undefined,
        idempotencyKey
          ? { headers: { 'Idempotency-Key': idempotencyKey } }
          : undefined,
      )
      .then(r => r.data),

  /**
   * Cancela orphan action (status=cancelled).
   * Backend: POST /api/v1/tenants/{tid}/finops/idle-resources/{id}/cancel
   */
  cancelOrphan: (tenantId: number, actionId: number) =>
    api
      .post<FinopsOrphanAction>(
        `/v1/tenants/${tenantId}/finops/idle-resources/${actionId}/cancel`,
      )
      .then(r => r.data),

  /**
   * Lista orphan actions com filtros + cursor pagination.
   * Backend: GET /api/v1/tenants/{tid}/finops/idle-resources/actions
   */
  listOrphanActions: (
    tenantId: number,
    params: {
      status?: string
      resource_type?: string
      account_id?: string
      min_savings?: number
      cursor?: string
      limit?: number
    } = {},
  ) =>
    api
      .get<FinopsOrphanActionListResponse>(
        `/v1/tenants/${tenantId}/finops/idle-resources/actions`,
        { params },
      )
      .then(r => r.data),

  /**
   * Detalhe de orphan action por id.
   * Backend: GET /api/v1/tenants/{tid}/finops/idle-resources/actions/{id}
   */
  getOrphanAction: (tenantId: number, actionId: number) =>
    api
      .get<FinopsOrphanAction>(
        `/v1/tenants/${tenantId}/finops/idle-resources/actions/${actionId}`,
      )
      .then(r => r.data),

  // Budget unificado: configurar/ler budget agora é via useTenantBudget*
  // (POST/GET /v1/tenants/{id}/budget/me → budget_thresholds). Os métodos
  // configureBudget/getBudget (Sistema B, tenant_budgets) foram removidos
  // na unificação de budget (2026-06-02).

  // ============================================================
  // SP4 — Recomendações unificadas (finops_recommendations)
  // Paths canônicos por-tenant: /api/v1/tenants/{tid}/finops/recommendations/*
  // Corrige o mismatch de path dos idle-resources (§1.5/§5.7 da spec SP4).
  // As funções *Orphan acima (idle-resources/*) seguem válidas durante o
  // soak do alias deprecado — a aba de detecção de órfãos ainda as usa.
  // ============================================================

  /**
   * Lista recomendações do tenant (escopado), cursor pagination.
   * Backend: GET /api/v1/tenants/{tid}/finops/recommendations
   */
  listRecommendations: (
    tenantId: number,
    params: FinopsRecommendationListParams = {},
  ) =>
    api
      .get<FinopsRecommendationListResponse>(
        `/v1/tenants/${tenantId}/finops/recommendations`,
        { params },
      )
      .then(r => r.data),

  /**
   * Detalhe de recomendação por id.
   * Backend: GET /api/v1/tenants/{tid}/finops/recommendations/{rec_id}
   */
  getRecommendation: (tenantId: number, recId: number) =>
    api
      .get<FinopsRecommendation>(
        `/v1/tenants/${tenantId}/finops/recommendations/${recId}`,
      )
      .then(r => r.data),

  /**
   * Promove detecção → recommended. Requer finops:recommend (dual-accept c/
   * legado finops:recommend_cleanup no backend).
   * Backend: POST .../recommendations/{rec_id}/recommend
   */
  recommendRecommendation: (tenantId: number, recId: number) =>
    api
      .post<FinopsRecommendationActionResponse>(
        `/v1/tenants/${tenantId}/finops/recommendations/${recId}/recommend`,
      )
      .then(r => r.data),

  /**
   * Aprova recommended → approved. Requer finops:execute.
   * Backend: POST .../recommendations/{rec_id}/approve
   */
  approveRecommendation: (tenantId: number, recId: number) =>
    api
      .post<FinopsRecommendationActionResponse>(
        `/v1/tenants/${tenantId}/finops/recommendations/${recId}/approve`,
      )
      .then(r => r.data),

  /**
   * Enfileira execução approved → executing. Requer finops:execute.
   * Idempotency-Key opcional. Backend: POST .../recommendations/{rec_id}/execute
   */
  executeRecommendation: (
    tenantId: number,
    recId: number,
    idempotencyKey?: string,
  ) =>
    api
      .post<FinopsRecommendationExecuteResponse>(
        `/v1/tenants/${tenantId}/finops/recommendations/${recId}/execute`,
        undefined,
        idempotencyKey
          ? { headers: { 'Idempotency-Key': idempotencyKey } }
          : undefined,
      )
      .then(r => r.data),

  /**
   * Cancela recommended|approved → cancelled. Owner OU finops:execute.
   * Backend: POST .../recommendations/{rec_id}/cancel
   */
  cancelRecommendation: (tenantId: number, recId: number) =>
    api
      .post<FinopsRecommendationActionResponse>(
        `/v1/tenants/${tenantId}/finops/recommendations/${recId}/cancel`,
      )
      .then(r => r.data),

  /**
   * Descarta detected|recommended → dismissed. Requer finops:recommend.
   * Backend: POST .../recommendations/{rec_id}/dismiss
   */
  dismissRecommendation: (tenantId: number, recId: number) =>
    api
      .post<FinopsRecommendationActionResponse>(
        `/v1/tenants/${tenantId}/finops/recommendations/${recId}/dismiss`,
      )
      .then(r => r.data),

  /**
   * Dispara detecção sob demanda para o tenant. Requer finops:refresh.
   * Backend: POST /api/v1/tenants/{tid}/finops/recommendations/refresh
   */
  refreshRecommendations: (tenantId: number, region?: string) =>
    api
      .post<FinopsRecommendationRefreshResponse>(
        `/v1/tenants/${tenantId}/finops/recommendations/refresh`,
        region ? { region } : undefined,
      )
      .then(r => r.data),

  /**
   * Economia agregada de plataforma (estimado + realizado, podado por scope).
   * Backend: GET /api/v1/finops/recommendations/savings
   */
  getRecommendationSavings: () =>
    api
      .get<FinopsRecommendationSavings>('/v1/finops/recommendations/savings')
      .then(r => r.data),

  /**
   * Lista recomendações de plataforma. O escopo é resolvido no backend:
   * admin/devops → todos os tenants; demais → tenants do usuário.
   * Backend: GET /api/v1/finops/recommendations
   */
  listRecommendationsPlatform: (params?: FinopsRecommendationListParams) =>
    api
      .get<FinopsRecommendationListResponse>('/v1/finops/recommendations', { params })
      .then(r => r.data),
}

// ============================================================
// SP5 — Assinaturas de relatório (agendado/recorrente) + export servidor
// Rotas de plataforma: /api/v1/finops/report-subscriptions(+/{id}/send-now,/runs)
// Export auditado: POST /api/v1/finops/reports/org-tree/export
// Tela gated por finops_reports_schedule_enabled; mutações exigem finops:export.
// ============================================================

export type FinopsReportFrequency = 'weekly' | 'monthly'
export type FinopsReportFormat = 'pdf' | 'csv' | 'xls'

/**
 * Row de `finops_report_subscriptions` (backend devolve `SELECT *`).
 * `tenant_id` null = assinatura global (org-wide). `report_format` é o nome
 * da coluna no banco (o body de criação usa `format`).
 */
export interface FinopsReportSubscription {
  id: number
  tenant_id: number | null
  channel: string
  recipients: string[]
  frequency: FinopsReportFrequency
  report_format: FinopsReportFormat
  hour_brt: number
  enabled: boolean
  created_by: number | null
  updated_by: number | null
  created_at: string
  updated_at: string
}

/** Status de uma run (fila/execução) de assinatura. */
export type FinopsReportRunStatus =
  | 'pending'
  | 'running'
  | 'success'
  | 'sent'
  | 'failed'
  | 'skipped'

/** Row de `finops_report_runs` (histórico de execuções). */
export interface FinopsReportRun {
  id: number
  subscription_id: number
  report_id: number | null
  status: FinopsReportRunStatus
  scheduled_for: string
  sent_count: number
  detail: string | null
  created_at: string
  finished_at: string | null
}

/** Body de criação (POST /report-subscriptions). `format`→report_format. */
export interface FinopsReportSubscriptionCreate {
  tenant_id?: number | null
  recipients: string[]
  frequency: FinopsReportFrequency
  format: FinopsReportFormat
  hour_brt: number
  enabled?: boolean
}

/** Body de PATCH parcial (todos opcionais; tenant_id não é editável). */
export interface FinopsReportSubscriptionUpdate {
  recipients?: string[]
  frequency?: FinopsReportFrequency
  format?: FinopsReportFormat
  hour_brt?: number
  enabled?: boolean
}

/** Resposta de POST /{id}/send-now. */
export interface FinopsReportSendNowResponse {
  run_id: number
  outcome: string | null
  sent: number
  detail: string | null
}

/**
 * Lista as assinaturas do escopo do caller (gestor: seus tenants; admin: todas
 * + globais). Requer finops:read. Backend: GET /v1/finops/report-subscriptions
 */
export function listReportSubscriptions(): Promise<FinopsReportSubscription[]> {
  return api
    .get<{ items: FinopsReportSubscription[] }>('/v1/finops/report-subscriptions')
    .then((r) => r.data.items)
}

/**
 * Cria uma assinatura. Requer finops:export (global tenant_id=null só
 * admin:platform). Backend: POST /v1/finops/report-subscriptions
 */
export function createReportSubscription(
  body: FinopsReportSubscriptionCreate,
): Promise<{ id: number; subscription: FinopsReportSubscription }> {
  return api
    .post<{ id: number; subscription: FinopsReportSubscription }>(
      '/v1/finops/report-subscriptions',
      body,
    )
    .then((r) => r.data)
}

/**
 * Atualiza parcialmente uma assinatura. Requer finops:export + escopo.
 * Backend: PATCH /v1/finops/report-subscriptions/{id}
 */
export function patchReportSubscription(
  id: number,
  body: FinopsReportSubscriptionUpdate,
): Promise<{ subscription: FinopsReportSubscription }> {
  return api
    .patch<{ subscription: FinopsReportSubscription }>(
      `/v1/finops/report-subscriptions/${id}`,
      body,
    )
    .then((r) => r.data)
}

/**
 * Remove uma assinatura. Requer finops:export + escopo.
 * Backend: DELETE /v1/finops/report-subscriptions/{id}
 */
export function deleteReportSubscription(
  id: number,
): Promise<{ ok: boolean; id: number }> {
  return api
    .delete<{ ok: boolean; id: number }>(`/v1/finops/report-subscriptions/${id}`)
    .then((r) => r.data)
}

/**
 * Dispara a assinatura agora (one-shot, fora da agenda). Requer finops:export.
 * Backend: POST /v1/finops/report-subscriptions/{id}/send-now
 */
export function sendReportNow(id: number): Promise<FinopsReportSendNowResponse> {
  return api
    .post<FinopsReportSendNowResponse>(
      `/v1/finops/report-subscriptions/${id}/send-now`,
    )
    .then((r) => r.data)
}

/**
 * Histórico de execuções (runs) de uma assinatura. Requer finops:read + escopo.
 * Backend: GET /v1/finops/report-subscriptions/{id}/runs
 */
export function getReportRuns(
  id: number,
  limit = 50,
): Promise<FinopsReportRun[]> {
  return api
    .get<{ items: FinopsReportRun[] }>(
      `/v1/finops/report-subscriptions/${id}/runs`,
      { params: { limit } },
    )
    .then((r) => r.data.items)
}

/**
 * Exporta o Relatório por Organização gerado NO SERVIDOR (csv/xls/pdf),
 * escopado às contas do caller e AUDITADO (finops:export). Recebe os bytes
 * (Content-Disposition: attachment) e dispara o download no browser.
 *
 * Substitui o export client-side (OrgTreeExporters) — fecha o GAP-5.
 * Backend: POST /v1/finops/reports/org-tree/export (body {format, ym, source})
 */
export async function exportOrgTreeServer(
  format: FinopsReportFormat,
  payload: { ym?: string; source?: 'snapshot' | 'athena' } = {},
): Promise<void> {
  const r = await api.post(
    '/v1/finops/reports/org-tree/export',
    { format, ...payload },
    { responseType: 'blob', timeout: 95000 },
  )
  // Nome de arquivo: usa o Content-Disposition do servidor quando presente,
  // senão monta a partir do mês + extensão.
  const cd = (r.headers?.['content-disposition'] as string | undefined) ?? ''
  const match = /filename="?([^"]+)"?/.exec(cd)
  const ym = payload.ym ?? new Date().toISOString().slice(0, 7)
  const filename = match?.[1] ?? `relatorio-organizacao-${ym}.${format}`

  const blob = r.data as Blob
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  a.click()
  setTimeout(() => URL.revokeObjectURL(url), 1000)
}

// ============================================================
// SP3 — Anomalias de custo (CE Anomaly Detector)
// ============================================================

/**
 * Item de anomalia retornado por GET /api/v1/finops/anomalies.
 * Shape: dict row de finops_events (event_source='ce_anomaly').
 */
export interface FinopsAnomalyItem {
  id: number
  external_id: string
  tenant_id: number
  aws_account_id: string | null
  aws_region: string | null
  event_source: string
  event_name: string
  detail_type: string | null
  severity: 'low' | 'medium' | 'high'
  outcome: string
  error_code: string | null
  event_time: string            // ISO datetime string
  received_at: string           // ISO datetime string
  acknowledged_at: string | null
  acknowledged_by: number | null
  payload: Record<string, unknown>
}

export interface FinopsAnomalyListResponse {
  items: FinopsAnomalyItem[]
  has_more: boolean
  next_cursor: string | null
}

/**
 * Lista anomalias de custo (event_source='ce_anomaly'), podadas ao escopo do caller.
 * Retorna lista vazia sem erro quando o caller não tem acesso.
 * Backend: GET /api/v1/finops/anomalies
 */
export function getAnomalies(params?: {
  severity?: 'low' | 'medium' | 'high'
  aws_account_id?: string
  acknowledged?: boolean
  cursor?: string
  limit?: number
}): Promise<FinopsAnomalyListResponse> {
  return api
    .get<FinopsAnomalyListResponse>('/v1/finops/anomalies', { params })
    .then((r) => r.data)
}
